import { useId, useRef, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../../../components/Icon';
import { isMeshStalled, type MeshAsset, type MeshProcessingStatus } from '../../../../types/admin';
import styles from './PlanOptions.module.scss';
import { formatByteSize } from './pricing';

const STATUS_CLASS: Record<MeshProcessingStatus, string> = {
  pending: styles.statusPending,
  processing: styles.statusProcessing,
  ready: styles.statusReady,
  failed: styles.statusFailed,
};

const STATUS_KEY: Record<MeshProcessingStatus, string> = {
  pending: 'planOptions.meshStatusPending',
  processing: 'planOptions.meshStatusProcessing',
  ready: 'planOptions.meshStatusReady',
  failed: 'planOptions.meshStatusFailed',
};

export type MeshUploadSlotProps = {
  /** Visible label for the cell, e.g. "Верхня · Зараз". */
  readonly label: string;
  /**
   * Accessible name for the cell's controls, when the visible label is not
   * unique on its own. The movement grid repeats "Верхня"/"Нижня" once per
   * step, so it qualifies them with the step ("Крок 0 · Верхня") while the
   * visible text stays short under the step heading. Defaults to `label`.
   */
  readonly accessibleLabel?: string;
  readonly mesh: MeshAsset | null;
  /** True only while THIS slot has a request in flight. */
  readonly isBusy: boolean;
  readonly disabled?: boolean;
  readonly onSelectFile: (file: File) => void;
  readonly onDelete: () => void;
  /** Retry GLB generation for a mesh whose conversion failed. */
  readonly onReprocess?: () => void;
};

/**
 * One upper/lower x current/result cell of a plan option's 3D grid.
 *
 * Uploading over an existing mesh replaces it server-side (the old row and its
 * objects are dropped), so the slot never shows more than one file.
 *
 * The file input is rendered *before* its label so the CSS sibling selector can
 * paint a focus ring — the input itself is visually hidden.
 */
export function MeshUploadSlot({
  label,
  accessibleLabel,
  mesh,
  isBusy,
  disabled = false,
  onSelectFile,
  onDelete,
  onReprocess,
}: MeshUploadSlotProps) {
  const { t } = useTranslation();
  const controlLabel = accessibleLabel ?? label;
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const isLocked = disabled || isBusy;

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    // Reset first so re-picking the same filename fires `change` again.
    if (inputRef.current) inputRef.current.value = '';
    if (file) onSelectFile(file);
  }

  const uploadLabel = mesh ? t('planOptions.meshReplace') : t('planOptions.meshUpload');
  const hasFailed = mesh?.processing_status === 'failed';
  // Server-truth staleness: the row has been converting far longer than it should.
  const hasStalled = mesh ? isMeshStalled(mesh, Date.now()) : false;

  return (
    <div className={`${styles.slot} ${mesh ? '' : styles.slotEmpty}`} data-testid="mesh-slot">
      <div className={styles.slotHeader}>
        <span className={styles.slotLabel}>{label}</span>
        {mesh ? (
          <span
            className={`${styles.statusPill} ${STATUS_CLASS[mesh.processing_status]}`}
            data-testid="mesh-status"
          >
            {t(STATUS_KEY[mesh.processing_status])}
          </span>
        ) : (
          <span className={styles.statusPill}>{t('planOptions.meshStatusEmpty')}</span>
        )}
      </div>

      {mesh && (
        <span className={styles.slotMeta}>
          {mesh.original_filename ?? t('planOptions.meshUntitledFile')}
          {/* The size is absent while a replacement is still being stored. */}
          {typeof mesh.source_byte_size === 'number'
            ? ` · ${formatByteSize(mesh.source_byte_size)}`
            : ''}
        </span>
      )}

      {hasFailed && mesh.processing_error && (
        <p className={styles.slotError} role="alert">
          {mesh.processing_error}
        </p>
      )}

      {hasStalled && (
        <p className={styles.pollHint} role="status" data-testid="mesh-stalled">
          {t('planOptions.meshStalled')}
        </p>
      )}

      <div className={styles.slotActions}>
        <input
          id={inputId}
          ref={inputRef}
          type="file"
          accept=".stl,model/stl,application/sla,application/vnd.ms-pki.stl"
          className={styles.fileInput}
          onChange={handleChange}
          disabled={isLocked}
          aria-label={`${uploadLabel} — ${controlLabel}`}
        />
        <label
          className={`${styles.uploadLabel} ${isLocked ? styles.uploadLabelDisabled : ''}`}
          htmlFor={inputId}
        >
          <Icon name="attach" size={12} />
          &nbsp;{uploadLabel}
        </label>
        {(hasFailed || hasStalled) && onReprocess && (
          <button
            type="button"
            className={styles.uploadLabel}
            onClick={onReprocess}
            disabled={isLocked}
            aria-label={`${t('planOptions.meshReprocess')} — ${controlLabel}`}
          >
            {t('planOptions.meshReprocess')}
          </button>
        )}
        {mesh && (
          <button
            type="button"
            className={styles.uploadLabel}
            onClick={onDelete}
            disabled={isLocked}
            aria-label={`${t('planOptions.meshDelete')} — ${controlLabel}`}
          >
            {t('planOptions.meshDelete')}
          </button>
        )}
      </div>
    </div>
  );
}
